import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Tooltip, IconButton } from '@mui/material';
import { ArrowBack, Refresh, GetApp } from '@mui/icons-material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT  = '#e0f7fa';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return isNaN(dt) ? '-' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const BUCKET_COLORS = {
    '0-30':  { bg: '#dcfce7', color: '#166534' },
    '31-60': { bg: '#fef9c3', color: '#92400e' },
    '61-90': { bg: '#ffedd5', color: '#9a3412' },
    '90+':   { bg: '#fee2e2', color: '#b91c1c' },
};
const bucketStyle = (bucket) => {
    const key = Object.keys(BUCKET_COLORS).find(k => (bucket || '').includes(k.replace('+', ''))) || '';
    return BUCKET_COLORS[key] || BUCKET_COLORS['90+'];
};

const iconBtn = {
    color: '#fff',
    '&:hover': { background: 'rgba(255,255,255,0.15)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
    borderRadius: '6px', padding: '4px',
};

const VendorAgeingBillToBill = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const [vendors, setVendors]   = useState([]);
    const [vendorId, setVendorId] = useState('');
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && token) {
            fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token)
                .then(res => setVendors(res?.response_data || res || []))
                .catch(() => setVendors([]));
        }
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && vendorId) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const fetchReport = async () => {
        if (!vendorId) { setError('Please select a vendor'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseReportApi',
                `/VendorBillToBillAgeing/${companyId}/${branchId}/${vendorId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vendor ageing report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.SupplierName || '').toLowerCase().includes(q) ||
            (r.BillNo || '').toLowerCase().includes(q) ||
            (r.AgeBucket || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const grandTotals = useMemo(() => filtered.reduce((acc, r) => ({
        billAmount:        acc.billAmount        + (parseFloat(r.BillAmount)        || 0),
        paidAmount:        acc.paidAmount        + (parseFloat(r.PaidAmount)        || 0),
        outstandingAmount: acc.outstandingAmount + (parseFloat(r.OutstandingAmount) || 0),
    }), { billAmount: 0, paidAmount: 0, outstandingAmount: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'VendorAgeingBillToBill', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Supplier', 'Bill No', 'Bill Date', 'Due Date',
            'Bill Amount', 'Paid Amount', 'Outstanding', 'Age Days', 'Age Bucket', 'Due Days'];
        const rows = filtered.map((r, i) => [
            i + 1, r.SupplierName, r.BillNo, fmtDate(r.BillDate), fmtDate(r.PaymentDueDate),
            parseFloat(r.BillAmount) || 0, parseFloat(r.PaidAmount) || 0,
            parseFloat(r.OutstandingAmount) || 0, r.AgeDays ?? '', r.AgeBucket || '', r.DueDays ?? '',
        ]);

        await exportStyledExcel({
            reportTitle: 'VENDOR AGEING ANALYSIS - BILL TO BILL',
            sheetName: 'Vendor Ageing Bill To Bill',
            fileName: `VendorAgeingBillToBill_${companyId}.xlsx`,
            companyName,
            username,
            headers: headers,
            rows,
            totalsRow: ['', '', '', '', 'Totals',
                fmtN(grandTotals.billAmount), fmtN(grandTotals.paidAmount),
                fmtN(grandTotals.outstandingAmount), '', '', ''],
            columnWidths: [4, 22, 14, 12, 12, 14, 14, 14, 10, 12, 10],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <style>{`.report-search::placeholder { color: rgba(255,255,255,0.75) !important; }`}</style>
            <LogoLoader open={loading} message="Loading vendor ageing report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Vendor Ageing Analysis
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Bill To Bill</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <SearchableDropdown
                        value={vendorId}
                        onChange={v => setVendorId(v)}
                        options={[...vendors.map(v => ({ value: String(v.MasterId || v.Id), label: v.Name }))]}
                        placeholder="— Select Vendor —"
                        minWidth={200}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search bill / bucket..." value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(237, 231, 231, 0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Meta bar */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {filtered.length} records
                        {search && ` (filtered from ${data.length})`}
                        &nbsp;|&nbsp; Outstanding: <strong style={{ color: '#b91c1c' }}>{fmtN(grandTotals.outstandingAmount)}</strong>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 800, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Supplier</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Due Date</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amount</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#0a5f6b' }}>Outstanding</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Age Days</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Age Bucket</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Due Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {!vendorId ? 'Select a vendor to load report' : fetched ? 'No records found' : 'Select a vendor and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const bs = bucketStyle(r.AgeBucket);
                                const isOld = (r.AgeDays || 0) > 60;
                                return (
                                    <tr key={`${r.PurchaseId}-${i}`} style={{ background: isOld ? (i % 2 === 0 ? '#fff8f8' : '#fff0f0') : (i % 2 === 0 ? '#fff' : PRIMARY_LT) }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 500 }}>{r.SupplierName || '-'}</td>
                                        <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                                        <td style={{ ...tdBase, textAlign: 'center', color: (r.DueDays || 0) < 0 ? '#dc2626' : '#374151', fontWeight: (r.DueDays || 0) < 0 ? 600 : 400 }}>{fmtDate(r.PaymentDueDate)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.BillAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtN(r.PaidAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fff5f5' }}>{fmtN(r.OutstandingAmount)}</td>
                                        <td style={{ ...tdBase, textAlign: 'center', color: isOld ? '#dc2626' : '#374151', fontWeight: isOld ? 600 : 400 }}>{r.AgeDays ?? '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'center' }}>
                                            <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: bs.bg, color: bs.color }}>
                                                {r.AgeBucket || '-'}
                                            </span>
                                        </td>
                                        <td style={{ ...tdBase, textAlign: 'center', color: (r.DueDays || 0) < 0 ? '#dc2626' : '#374151', fontWeight: (r.DueDays || 0) < 0 ? 700 : 400 }}>{r.DueDays ?? '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: '#b2ebf2', fontWeight: 700 }}>
                                    <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.billAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(grandTotals.outstandingAmount)}</td>
                                    <td colSpan={3} style={{ ...tdBase }} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default VendorAgeingBillToBill;


